import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect, DistributiveOmit } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';

import { Loader } from 'components/UtilElements';
import { Dispatch } from 'redux';
import { SET_AUTH_STATUS, UPDATE_ACCESS_DATA } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';
import {
  getApplicationLaunchData,
  getContextCourseId,
  getContextUserId,
  getSessionToken,
  refreshContextCourseId,
  refreshContextUserId,
  refreshSessionToken,
  removeSessionToken,
  setContextCourseId,
  setContextUserId,
  setSessionToken,
} from 'utils/session';

interface GetAccessResponse extends MessageResponse, DistributiveOmit<SessionData, 'courseDetails'> {
  courseDetails: Pick<SessionData['courseDetails'], keyof CourseListItem>;
}

const getAccessData = (token: string, context: AppContext) => {
  return request<GetAccessResponse>({
    url: API.LTI_GET_ACCESS,
    params: process.env.REACT_APP_ENV === 'development' ? { token, context } : { token },
  });
};
interface ValidateAuthTokenResponse extends MessageResponse {
  isLoggedIn: NumericBoolean;
}

const validateAuthToken = (context: AppContext, courseId?: string) => {
  return request<ValidateAuthTokenResponse>({
    url: API.LTI_VALIDATE_ACCESS_TOKEN,
    method: 'POST',
    data: { context, courseId },
  });
};

interface PageError {
  code: string;
  message: string;
}

interface RouteParams {
  context: AppContext;
}

interface MainProps extends RouteComponentProps<RouteParams> {
  accessData: SessionData | null;
  setAuthorization(isAuthorized: boolean): void;
  setAccessData(payload: GetAccessResponse): Promise<void>;
}

const Main: FC<MainProps> = ({ setAccessData, accessData, setAuthorization, match }) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<PageError | null>(null);

  const initSession = useCallback(async () => {
    const { token, launchError } = getApplicationLaunchData();

    if (launchError) {
      return setError({
        code: '',
        message: launchError,
      });
    }

    if (!token) {
      return setError({
        code: '403',
        message: 'You are not authorised to access this app.',
      });
    }

    try {
      let isLoggedIn: NumericBoolean = 0;
      const authToken = getSessionToken();
      const contextCourseId = getContextCourseId();
      const contextUserId = getContextUserId();
      const response = await getAccessData(token, match.params.context);

      if (response.isAuthenticated === 1) {
        setSessionToken(response.authToken);
        setContextCourseId(response.courseDetails.courseId);
        setContextUserId(response.userId);
        setAuthorization(true);
        // isLoggedIn = 1;
      } else if (
        authToken &&
        (response.context !== 'course' ||
          response.courseDetails.courseId === contextCourseId ||
          response.userId === contextUserId)
      ) {
        ({ isLoggedIn } = await validateAuthToken(response.context, contextCourseId));
      }

      if (isLoggedIn === 1) {
        // auth-token is still valid to reuse
        setAuthorization(true);
        refreshSessionToken(authToken!);
        refreshContextCourseId(response.courseDetails.courseId);
        refreshContextUserId(response.userId);
      } else {
        /**
         * auth-token is not valid or does not exists.
         * use sessionToken as temporary authToken until we get authToken.
         */
        setSessionToken(response.authToken ? response.authToken : response.sessionToken);
      }

      setAccessData(response);
    } catch (e) {
      console.error(e);
      removeSessionToken();
      setError({
        code: '500',
        message: 'Some error occurred, try reloading, if error persists please contact Acadly support team.',
      });
    }
  }, [match.params.context, setAccessData, setAuthorization]);

  useEffect(() => {
    if (!isInitializing) {
      setIsInitializing(true);
      initSession();
    }
  }, [isInitializing, initSession]);

  if (error) {
    return (
      <Redirect
        to={{
          pathname: ROUTES.MESSAGE_PAGE,
          state: {
            code: error.code,
            message: error.message,
          },
        }}
      />
    );
  }

  if (!accessData) {
    return (
      <div className='page__body'>
        <Loader />
      </div>
    );
  }

  return <Redirect to={ROUTES.AUTH} />;
};

const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setAuthorization: (isAuthorized: boolean) => {
      dispatch({
        type: SET_AUTH_STATUS,
        payload: isAuthorized,
      });
    },
    setAccessData: async (payload: GetAccessResponse) => {
      dispatch({ type: UPDATE_ACCESS_DATA, payload });
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Main);
